import { useState } from "react";
import { useResource } from "hooks/useResource";
import { getResults } from "services/content";
import PageHero from "components/shared/PageHero";
import SectionLabel from "components/shared/SectionLabel";
import Reveal from "components/shared/Reveal";
import Skeleton from "components/shared/Skeleton";

export default function ResultsPage() {
  const { data } = useResource(getResults);
  const [tab, setTab] = useState("ssc");
  const rows = data ? data[tab] || [] : [];
  const toppers = data?.toppers || [];

  return (
    <div className="animate-fadeIn">
      <PageHero
        title="Exam Results"
        sub="Consistently outstanding performance in national board examinations — a testament to our academic excellence."
        page="Results"
        bg="linear-gradient(140deg,#1a1200,#5a4100)"
      />
      <section className="max-w-container mx-auto px-4 sm:px-10 py-[68px]">
        {/* Tabs */}
        <div className="flex gap-2 border-b-2 border-line mb-10 flex-wrap">
          {[
            ["ssc", "SSC Results"],
            ["hsc", "HSC Results"],
          ].map(([k, l]) => {
            const on = tab === k;
            return (
              <button
                key={k}
                onClick={() => setTab(k)}
                className="px-6 py-3 bg-transparent font-sans text-[15px] cursor-pointer -mb-0.5"
                style={{
                  borderBottom: on ? "3px solid #9B7200" : "3px solid transparent",
                  color: on ? "#9B7200" : "#5c5c5c",
                  fontWeight: on ? 700 : 400,
                }}
              >
                {l}
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="bg-white border border-line rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] mb-14 overflow-x-auto">
          <div key={tab} className="min-w-[600px] animate-fadeIn">
            <div className="bg-green px-6 py-3.5 grid grid-cols-5 gap-4 font-bold text-white text-[13px] tracking-[0.04em]">
              {["Year", "Appeared", "GPA-5", "Pass Rate", "Award"].map((h) => (
                <span key={h}>{h}</span>
              ))}
            </div>
            {!data
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-5 gap-4 px-6 py-4 border-b border-line"
                  >
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Skeleton key={j} className="h-5 w-16" />
                    ))}
                  </div>
                ))
              : rows.map((row, i) => (
                  <div
                    key={row.year}
                    className="grid grid-cols-5 gap-4 px-6 py-4 border-b border-line text-sm items-center"
                    style={{ background: i === 0 ? "#e6f2ed" : "transparent" }}
                  >
                    <span className="font-serif font-bold text-lg text-green-dark">
                      {row.year}
                    </span>
                    <span className="text-muted">{row.appear}</span>
                    <span className="font-bold text-green">{row.gpa5}</span>
                    <span
                      className="font-semibold"
                      style={{ color: i === 0 ? "#B8002B" : "#1c1c1c" }}
                    >
                      {row.pass}
                    </span>
                    <span
                      className="text-xs px-2.5 py-[3px] rounded-full w-fit"
                      style={{
                        background: row.award !== "—" ? "#f7efc8" : "transparent",
                        color: row.award !== "—" ? "#9B7200" : "#5c5c5c",
                        fontWeight: row.award !== "—" ? 700 : 400,
                      }}
                    >
                      {row.award}
                    </span>
                  </div>
                ))}
          </div>
        </div>

        {/* Toppers */}
        <div>
          <SectionLabel label="Board Toppers" />
          <h2 className="font-serif text-[32px] font-bold text-green-dark mb-7">
            Our Star Achievers
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {!data
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-[100px] rounded-[14px]" />
                ))
              : toppers.map(({ name, gpa, group, year }, i) => (
                  <Reveal
                    key={name}
                    delay={(i % 3) * 90}
                    className="bg-white border border-line rounded-[14px] px-5 py-[22px] flex gap-4 items-center shadow-[0_2px_10px_rgba(0,0,0,0.05)] transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="w-[52px] h-[52px] rounded-full bg-gold-light border-2 border-gold flex items-center justify-center text-[22px] font-bold text-gold font-serif shrink-0">
                      {name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-serif font-bold text-base text-green-dark">
                        {name}
                      </div>
                      <div className="text-xs text-muted mt-[3px]">
                        {group} · {year}
                      </div>
                      <div className="text-[13px] font-bold text-green mt-1">
                        GPA {gpa} 🏆
                      </div>
                    </div>
                  </Reveal>
                ))}
          </div>
        </div>
      </section>
    </div>
  );
}
